import { Box, Divider, Skeleton, tooltipClasses, Typography } from "@mui/material";
import { StyledCard } from "./components/StyledComponents";
import CustomIcon from "../../Components/CustomIcon";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import InfoIcon from "../../Components/Icons/InfoIcon";
import { formatCompactDistanceKm, formatCompactEnergyKWh } from "../../Helper/Formatters/NumberFormatter";

/**
 * Custom platform FleetMetricsCard.
 *
 * Difference from Coulomb version: accepts a `metricsConfig` prop sourced from
 * customDashboards[activeKey].overview.fleetMetrics. Each metric is only rendered
 * when its config key is not explicitly false.
 *
 * Example config: { energyConsumed: true, distanceTravelled: true,
 *                   energyDecimals: 1, distanceDecimals: 0 }
 */
const FleetMetricsCard = ({ stats, loading, metricsConfig = {} }) => {
  const { totalEnergyConsumed, totalDistanceTravelled } = stats;

  const showEnergy = metricsConfig.energyConsumed !== false;
  const showDistance = metricsConfig.distanceTravelled !== false;

  // Fleet totals get large, so both step up to bigger units (kWh -> MWh -> GWh,
  // km -> k km -> M km) instead of printing every digit. Precision comes from the
  // same metricsConfig block (energyDecimals / distanceDecimals); absent config
  // falls back to one decimal for energy and whole units for distance.
  const energyLabel = formatCompactEnergyKWh(totalEnergyConsumed, metricsConfig.energyDecimals) ?? "-";
  const distanceLabel = formatCompactDistanceKm(totalDistanceTravelled, metricsConfig.distanceDecimals) ?? "-";

  if (!showEnergy && !showDistance) return null;

  return (
    <StyledCard>
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 1, height: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="fw500s16lh20c000000">Fleet Metrics</Typography>
          <Typography variant="fw400s12c686868" sx={{ lineHeight: "20px" }}>Yesterday</Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {showEnergy && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1.5 }}>
              <CustomIcon name="charge-icon" sx={{ color: "#4D44D8", fontSize: 20 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="fw400s14c000000" sx={{ lineHeight: "20px" }}>Energy Consumed</Typography>
                <PrimaryTooltip
                  title='Total charging+regeneration energy into battery, as per "Accumulated Charging Energy"'
                  sx={{ [`& .${tooltipClasses.tooltip}`]: { maxWidth: "200px", textAlign: "left !important" } }}
                >
                  <InfoIcon sx={{ width: 14, height: 14, "&:hover": { color: "#4D44D8" } }} />
                </PrimaryTooltip>
              </Box>
              {loading
                ? <Skeleton variant="text" width={80} height={24} />
                : <Typography variant="fw500s16lh20c000000">{energyLabel}</Typography>
              }
            </Box>
          )}

          {showEnergy && showDistance && (
            <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4" }} />
          )}

          {showDistance && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1.5 }}>
              <CustomIcon name="distance-icon" sx={{ color: "#4D44D8", fontSize: 20 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="fw400s14c000000" sx={{ lineHeight: "20px" }}>Odometer</Typography>
                <PrimaryTooltip
                  title='Total distance covered as per "odometer"'
                  sx={{ [`& .${tooltipClasses.tooltip}`]: { maxWidth: "150px", textAlign: "left !important" } }}
                >
                  <InfoIcon sx={{ width: 14, height: 14, "&:hover": { color: "#4D44D8" } }} />
                </PrimaryTooltip>
              </Box>
              {loading
                ? <Skeleton variant="text" width={80} height={24} />
                : <Typography variant="fw500s16lh20c000000">{distanceLabel}</Typography>
              }
            </Box>
          )}
        </Box>
      </Box>
    </StyledCard>
  );
};

export default FleetMetricsCard;
